import {
  AuthTokenErrorType,
  IAuthTokenError,
  ISourceTokenDebugger,
} from '@portal/env/stores/authTokens/authTokenTypes';
import { SourceTokenDebugger } from '@portal/env/stores/authTokens/TokenDebugger';
import { IFetchHelper } from '@portal/env/stores/authTokens/IFetchHelper';
import { FetchHelper } from '@portal/env/stores/authTokens/FetchHelper';
import { tryParseJSON } from '@common/utils/tryParseJSON';
import { clientConfig } from '@portal/env/config';

const config = clientConfig();

/**
 * Just has the responsibility of refreshing the nexudus token.
 *
 * This has been refactored out of the NexudusTokenStore so support mocking independently.
 */
export class NexudusTokenRefresher {
  public readonly source = 'nexudusToken';

  constructor(
    private readonly tokenDebugger: ISourceTokenDebugger = new SourceTokenDebugger(
      this.source,
    ),
    private readonly fetchHelper: IFetchHelper = new FetchHelper(),
  ) {}

  public async tryFetchToken(headers: Record<string, string>): Promise<
    | {
        access_token: string;
        expires_in: number;
      }
    | IAuthTokenError
  > {
    const url = `${config.authServiceUrl}/api/nexudus/token`;
    try {
      this.tokenDebugger.addEvent({
        action: 'updateAttempt',
        message: 'Attempting to update nexudus token using bruntwood API',
      });

      const result = await this.fetchHelper.fetch(url, {
        method: 'GET',
        throwOnHttpError: false,
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
      });

      const body = tryParseJSON(await result.text()) as
        | {
            error?: string;
            access_token?: string;
            expires_in?: number;
          }
        | undefined;

      if (!result.ok) {
        const message = body?.error || 'Unknown error';
        this.tokenDebugger.addEvent({
          action: 'updateFailed',
          message: `Could not update nexudus token: ${message}`,
          error: new Error(message),
        });
        return {
          source: this.source,
          type: AuthTokenErrorType.REFRESH_FAILED,
          message: `Could not update nexudus token: ${message}`,
        };
      }

      if (!body || !body.access_token || !body.expires_in) {
        const message = 'Refresh seemed to succeed but no token was returned';
        this.tokenDebugger.addEvent({
          action: 'updateFailed',
          message: `Could not update nexudus token: ${message}`,
          error: new Error(message),
        });

        return {
          source: this.source,
          type: AuthTokenErrorType.REFRESH_FAILED,
          message: `Could not update nexudus token: ${message}`,
        };
      }

      return {
        access_token: body.access_token,
        expires_in: body.expires_in,
      };
    } catch (e: any) {
      this.tokenDebugger.addEvent({
        action: 'updateFailed',
        message: `Could not update nexudus token: ${e?.message}`,
        error: e,
      });

      return {
        source: this.source,
        type: AuthTokenErrorType.REFRESH_FAILED,
        message: `Could not update nexudus token: ${e?.message}`,
      };
    }
  }
}
